import defaultAttributes from "backend/defaultAttributes";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import useTopElements from "backend/hooks/model/useTopElements";
import { ICardElementWithChildren } from "backend/model/model";
import CascadeToolbar from "components/toolbars/CascadeToolbar";
import { DivisionCard } from "design/components/cards/DivisionCard";
import MeasureGridArea from "design/components/grids/MeasureGridArea";
import PageContainer from "design/layouts/PageContainer";
import Page from "design/pages/Page";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import Query from "qpr-base/querybuilder/Query";
import { getFileApiURL } from "qpr-base/utils/url-utils";
import React from "react";
import { useQuery } from "react-query";

const CascadePage = () => {
  const ctx = useAppContext();
  const link = useLink();
  const { data: topElems, isLoading: topElementsLoading } = useTopElements();
  const rootSCId = topElems?.find(
    (el) => el.scorecardsymbol === "L0"
  )?.scorecardid;

  const { data: periodlevels } = usePeriodLevels();
  const {
    data,
    isLoading: isDataLoading,
    isError,
    isPreviousData,
    isRefetching,
  } = useHierarchy(rootSCId);

  const { data: topElements, isLoading: isLoadingTopElements } = useQuery(
    `topelements-cascade-${ctx.period}`,
    () => {
      return Query(data?.map((el) => el.id))
        .topelement()
        .attributes(...defaultAttributes(ctx.period, periodlevels!))
        .fetchValues<ICardElementWithChildren>()
        .then((list) =>
          list.sort((a, b) => {
            if (!a.symbol || !b.symbol) return 0;
            return a.symbol.localeCompare(b.symbol);
          })
        );
    },
    { enabled: !!data && !!periodlevels }
  );

  return (
    <Page
      isLoading={
        !topElements ||
        !topElems ||
        !data ||
        topElementsLoading ||
        isLoadingTopElements ||
        isDataLoading ||
        isRefetching ||
        isPreviousData
      }
      Toolbar={CascadeToolbar}
    >
      <PageContainer width="100%">
        <MeasureGridArea>
          {(data || [])
            .sort((a, b) => {
              if (!a.symbol || !b.symbol) return 0;
              return a.symbol.localeCompare(b.symbol);
            })
            .map((sc) => {
              const topE = topElems?.find((te) => te.scorecardid === sc.id);
              const top = topElements?.find((t) => t.id === topE?.id);
              if (!top) {
                return null;
              }

              const sectionTitle =
                getTranslatedAttribute(
                  topElems?.find((te) => te.id === top.id),
                  "Name",
                  ctx.language
                ) || sc.name;

              const children: ICardElementWithChildren[] = sc.children || [];

              if (!children.length) {
                return null;
              }

              return (
                <div
                  key={sc.id}
                  style={{
                    width: "100%",
                    marginBottom: "2.5rem",
                    padding: "1.5rem 1.8rem",
                    borderRadius: "1.2rem",
                    boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
                  }}
                >
                  <h2
                    style={{
                      margin: 0,
                      marginBottom: "1.2rem",
                      color: "#102975",
                      fontSize: "1.2rem",
                      fontWeight: 600,
                    }}
                  >
                    {sectionTitle}
                  </h2>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                    }}
                  >
                    {children.map((child) => {
                      const translatedName =
                        getTranslatedAttribute(child, "Name", ctx.language) ||
                        child.name;

                      return (
                        <div
                          key={child.id}
                          style={{
                            display: "flex",
                          }}
                        >
                          <DivisionCard
                            translatedName={translatedName}
                            topElement={top}
                            rangeColor={top.rangecolor}
                            rangeName={top.rangename}
                            cardTitle={t(`cascade.Division`)}
                            departmentIcon={getFileApiURL(
                              `/icons/divisions/DEP_1.svg`
                            )}
                            backgroundImage={getFileApiURL(
                              `/images/divisions/divisions.png`
                            )}
                            typenames={[
                              {
                                name: "Scorecard",
                                title: t("cascade.Departments"),
                              },
                            ]}
                            scorecard={child}
                            onSelect={() => {
                              if (child.symbol)
                                link({
                                  page: "strategymap",
                                  meaId: child.topelement,
                                });
                            }}
                            onSelectChild={(item) => {
                              if (item.symbol)
                                link({
                                  page: "strategymap",
                                  meaId: item.topelement,
                                });
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </MeasureGridArea>
      </PageContainer>
    </Page>
  );
};
export default CascadePage;
